import { N8nModule } from '../../types';

export const module1: N8nModule = {
  id: 'n8n-intro',
  title: 'Introduction to n8n',
  description: 'Learn the fundamentals of n8n, including installation, configuration, and creating your first workflow.',
  duration: '1.5 hours',
  objectives: [
    'Understand what n8n is and its advantages over traditional automation tools',
    'Install n8n using Docker, npm, or cloud deployment',
    'Navigate the n8n interface and understand core components',
    'Create your first automation workflow from scratch',
    'Understand nodes, connections, and workflow execution'
  ],
  prerequisites: [
    'Basic understanding of web technologies',
    'Familiarity with command line (for self-hosted installation)',
    'Understanding of API concepts (helpful but not required)'
  ],
  content: `# Introduction to n8n

## What is n8n?

n8n (nodemation) is a powerful, open-source workflow automation tool that allows you to connect applications and automate tasks without writing extensive code. Unlike proprietary tools like Zapier or Make.com, n8n offers a **fair-code** licensed platform that can be self-hosted, giving you complete control over your data and automation infrastructure.

### Key Features

- **500+ Pre-built Integrations**: Connect to popular services like Gmail, Slack, GitHub, PostgreSQL, AWS, and more
- **Visual Workflow Builder**: Node-based interface for creating complex automation logic
- **Self-hosted or Cloud**: Choose between complete data privacy (self-hosted) or convenience (n8n Cloud)
- **Unlimited Workflows**: No artificial limits on workflow complexity or execution count
- **Custom Code**: Write JavaScript directly in workflows for ultimate flexibility
- **Active Community**: 35,000+ GitHub stars and growing ecosystem

### Why Choose n8n?

**Open Source Advantage**
- Full access to source code
- Community-driven development
- No vendor lock-in
- Transparent security practices

**Cost Effective**
- Free self-hosted version (unlimited executions)
- Pay only for cloud hosting if you choose that route
- No per-execution pricing like Zapier

**Developer Friendly**
- JavaScript-based custom functions
- REST API for programmatic control
- Webhook triggers for event-driven automation
- Docker-ready for easy deployment

**Enterprise Ready**
- Queue mode for high-volume workflows
- PostgreSQL backend for scalability
- OAuth and credential management
- Detailed execution logs

---

## Installation Methods

n8n offers multiple installation options to suit different use cases and technical skill levels.

### Option 1: Docker Installation (Recommended)

Docker provides the easiest and most reliable way to run n8n with persistent data storage.

**Quick Start with Docker:**

\`\`\`bash
# Pull the latest n8n image
docker pull n8nio/n8n

# Run n8n with persistent data storage
docker run -it --rm \\
  --name n8n \\
  -p 5678:5678 \\
  -v ~/.n8n:/home/node/.n8n \\
  n8nio/n8n
\`\`\`

**Docker Compose (Production-Ready):**

\`\`\`yaml
version: '3.8'

services:
  n8n:
    image: n8nio/n8n
    container_name: n8n
    restart: unless-stopped
    ports:
      - "5678:5678"
    environment:
      - N8N_BASIC_AUTH_ACTIVE=true
      - N8N_BASIC_AUTH_USER=admin
      - N8N_BASIC_AUTH_PASSWORD=your_secure_password
      - N8N_HOST=localhost
      - N8N_PORT=5678
      - N8N_PROTOCOL=http
      - NODE_ENV=production
      - WEBHOOK_URL=http://localhost:5678/
    volumes:
      - ./n8n_data:/home/node/.n8n
\`\`\`

After running, access n8n at: **http://localhost:5678**

### Option 2: npm Installation

For developers who prefer npm package management:

\`\`\`bash
# Install n8n globally
npm install n8n -g

# Start n8n
n8n

# Or with custom configuration
n8n start --tunnel
\`\`\`

### Option 3: n8n Cloud

For those who prefer managed hosting:

1. Visit [n8n.cloud](https://n8n.cloud)
2. Create an account
3. Start building workflows immediately
4. No infrastructure management required

**Cloud Benefits:**
- Automatic updates
- SSL certificates included
- Managed backups
- No server maintenance
- Free tier available

### Option 4: Desktop Application

n8n offers a desktop app for Windows, macOS, and Linux:

1. Download from [n8n.io/download](https://n8n.io/download)
2. Install like any desktop application
3. Launch and start building workflows

---

## n8n Interface Tour

Understanding the n8n interface is crucial for building effective workflows.

### Main Components

**1. Workflow Canvas**
- Central workspace where you build automation flows
- Drag and drop nodes to create workflows
- Zoom and pan to navigate complex workflows
- Grid snap for organized layouts

**2. Node Palette (Left Sidebar)**
- Search bar to find nodes quickly
- Categorized nodes:
  - **Triggers**: Webhook, Schedule, Email Trigger
  - **Actions**: HTTP Request, Database operations, Send Email
  - **Core**: IF, Switch, Merge, Loop
  - **Apps**: 500+ integrations (Slack, Google Sheets, GitHub, etc.)

**3. Node Settings Panel (Right Sidebar)**
- Configure selected node parameters
- Set credentials (API keys, OAuth tokens)
- Test node execution
- View node documentation

**4. Top Menu Bar**
- **Workflows**: List all your workflows
- **Credentials**: Manage API keys and authentication
- **Executions**: View workflow execution history
- **Settings**: Configure n8n preferences
- **Help**: Access documentation and community

**5. Workflow Controls**
- **Save**: Save workflow changes
- **Execute Workflow**: Run entire workflow manually
- **Activate**: Enable automatic execution (for trigger-based workflows)
- **Delete**: Remove current workflow

### Navigation Tips

💡 **Tip**: Press **Ctrl+F** (Cmd+F on Mac) to quickly search for nodes in the palette

💡 **Tip**: Use **Ctrl+Z** (Cmd+Z on Mac) to undo changes on the canvas

💡 **Tip**: Click the **?** icon on any node to access inline documentation

---

## Creating Your First Workflow

Let's build a simple but practical workflow: **Fetch weather data and send a notification**

### Step-by-Step Tutorial

**Step 1: Create New Workflow**

1. Click **"Workflows"** in the top menu
2. Click **"+ Add Workflow"**
3. Name your workflow: "Weather Notification"

**Step 2: Add a Schedule Trigger**

1. Click **"Add trigger node"** or **"+"** on canvas
2. Search for **"Schedule Trigger"**
3. Configure to run daily at 8:00 AM:
   - Mode: **Every Day**
   - Hour: **8**
   - Minute: **0**

**Step 3: Add HTTP Request Node**

1. Click **"+"** to add a new node
2. Search for **"HTTP Request"**
3. Configure:
   - Method: **GET**
   - URL: \`https://api.openweathermap.org/data/2.5/weather?q=London&appid=YOUR_API_KEY&units=metric\`
   - Response Format: **JSON**

💡 **Note**: Get a free API key from [OpenWeatherMap](https://openweathermap.org/api)

**Step 4: Add Function Node (Data Transformation)**

1. Add a **"Function"** node
2. Write JavaScript to format the weather data:

\`\`\`javascript
// Get weather data from previous node
const weather = $json.weather[0];
const temp = $json.main.temp;
const city = $json.name;

// Format message
return {
  message: \`Good morning! The weather in \${city} is \${weather.description} with a temperature of \${temp}°C.\`
};
\`\`\`

**Step 5: Add Send Email Node**

1. Add **"Send Email"** node
2. Configure:
   - **From Email**: your-email@gmail.com
   - **To Email**: recipient@example.com
   - **Subject**: "Daily Weather Update"
   - **Text**: \`{{ $json.message }}\`
3. Add Gmail credentials (OAuth or App Password)

**Step 6: Test Your Workflow**

1. Click **"Execute Workflow"** button
2. Watch nodes execute one by one
3. Check green checkmarks indicating success
4. Verify email received

**Step 7: Activate for Automatic Execution**

1. Toggle the **"Inactive/Active"** switch in top right
2. Workflow will now run automatically at 8:00 AM daily

---

## Understanding Nodes and Connections

### Node Types

**Trigger Nodes** (Start workflow)
- Schedule Trigger: Run on a time schedule
- Webhook: Trigger via HTTP request
- Email Trigger: Activate on incoming email
- File Trigger: Monitor file system changes

**Regular Nodes** (Process data)
- HTTP Request: Call external APIs
- Function: Execute custom JavaScript
- IF: Conditional branching
- Database: CRUD operations

**Output Nodes** (Send data)
- Send Email: Email notifications
- Slack: Post messages to Slack
- Discord: Send to Discord channels
- Database: Insert/Update records

### Connections

Nodes are connected with **arrows** that represent data flow:

- **Success Path** (solid line): Execute when previous node succeeds
- **Error Path** (dashed line): Execute when previous node fails
- **Multiple Outputs**: Nodes like IF have multiple output paths

### Data Flow

Each node receives data from the previous node via the **\`$json\`** variable:

\`\`\`javascript
// Access data from previous node
const temperature = $json.main.temp;

// Access data from specific node
const weatherData = $node["HTTP Request"].json;

// Access environment variables
const apiKey = $env.WEATHER_API_KEY;
\`\`\`

### Pinned Data

**Pinned data** allows you to test workflows without re-executing all nodes:

1. Execute a workflow once
2. Click on a node showing output
3. Click **"Pin data"** in the data panel
4. Future executions will use this data for testing

💡 **Best Practice**: Pin data during development to speed up testing

---

## Workflow Execution Modes

### Manual Execution

- Click **"Execute Workflow"** button
- Useful for testing and debugging
- Shows real-time execution progress
- View data at each step

### Automatic Execution

- Toggle **"Active"** switch
- Workflow runs automatically based on triggers
- Schedule Trigger: Time-based execution
- Webhook Trigger: Event-driven execution
- Email Trigger: Incoming email activation

### Execution History

View past workflow runs:
1. Click **"Executions"** in top menu
2. See all workflow runs with timestamps
3. Click any execution to view details
4. Debug failed executions
5. Re-run previous executions

---

## Common Use Cases for n8n

### Business Automation
- Lead capture from website forms → CRM
- Invoice generation and email delivery
- Customer onboarding workflows
- Order processing and fulfillment

### DevOps & IT
- Server monitoring and alert notifications
- Automated deployment pipelines
- Backup automation
- Log aggregation and analysis

### Marketing
- Social media post scheduling
- Email campaign automation
- Analytics data collection
- Content syndication

### Data Integration
- Sync data between databases
- ETL pipelines
- API-to-database workflows
- Real-time data transformation

---

## Next Steps

Now that you understand n8n basics, you're ready to:

1. ✅ Explore more node types in the palette
2. ✅ Build your own custom workflows
3. ✅ Set up credentials for your favorite services
4. ✅ Learn advanced features like error handling
5. ✅ Join the [n8n community forum](https://community.n8n.io)

In the next module, we'll dive deep into **Core n8n Concepts** including data flow, expressions, and error handling.

---

## Key Takeaways

- n8n is an open-source, self-hostable workflow automation platform
- Multiple installation options: Docker, npm, cloud, or desktop
- Visual node-based interface makes building workflows intuitive
- 500+ pre-built integrations with popular services
- Free for self-hosted use with unlimited workflows
- Active community and enterprise-ready features

**Remember**: Start simple, test frequently, and gradually build more complex workflows as you gain confidence!`,
  
  quickCheck: {
    questions: [
      {
        id: 'q1-licensing',
        question: "What is n8n's licensing model?",
        options: [
          "Fully proprietary software",
          "Fair-code licensed (Apache 2.0 with Commons Clause)",
          "GPL open source",
          "Commercial license only"
        ],
        correctAnswer: 1,
        explanation: "n8n uses a fair-code license (Apache 2.0 with Commons Clause), which allows self-hosting and modifications but restricts selling it as a service."
      },
      {
        id: 'q2-installation',
        question: "Which installation method is recommended for production deployments?",
        options: [
          "npm global installation",
          "Desktop application",
          "Docker with Docker Compose",
          "n8n Cloud only"
        ],
        correctAnswer: 2,
        explanation: "Docker with Docker Compose is recommended for production as it provides isolation, easy updates, and persistent data storage."
      },
      {
        id: 'q3-node-types',
        question: "What are the three main types of nodes in n8n?",
        options: [
          "Input, Output, Processing",
          "Trigger, Regular, Output",
          "Start, Middle, End",
          "Source, Transform, Destination"
        ],
        correctAnswer: 1,
        explanation: "n8n has three main node types: Trigger nodes (start workflows), Regular nodes (process data), and Output nodes (send data to destinations)."
      },
      {
        id: 'q4-webhook-testing',
        question: "How do you test a webhook trigger without external HTTP requests?",
        options: [
          "You cannot test webhooks locally",
          "Use the 'Execute Workflow' button for manual testing",
          "Only possible in production",
          "Requires third-party testing tools"
        ],
        correctAnswer: 1,
        explanation: "You can test the entire workflow including webhook triggers using the 'Execute Workflow' button, which simulates the trigger."
      },
      {
        id: 'q5-pinned-data',
        question: "What is 'pinned data' used for in n8n?",
        options: [
          "Saving workflow configurations permanently",
          "Testing workflows with sample data without re-executing previous nodes",
          "Preventing workflow modifications",
          "Sharing workflows with team members"
        ],
        correctAnswer: 1,
        explanation: "Pinned data allows you to lock sample output from a node, making testing faster by skipping re-execution of previous steps."
      },
      {
        id: 'q6-export-workflow',
        question: "How do you export a workflow in n8n?",
        options: [
          "Use the database backup feature",
          "Download as PDF from settings",
          "Click the workflow menu and select 'Download'",
          "Copy the entire canvas as an image"
        ],
        correctAnswer: 2,
        explanation: "Workflows can be exported as JSON files by clicking the workflow menu (three dots) and selecting 'Download', making them shareable and portable."
      }
    ]
  }
};
